import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { Download, Image as ImageIcon } from "@mui/icons-material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";

// Print-friendly theme
const theme = createTheme({
  typography: {
    fontFamily: "Arial, sans-serif",
  },
});

interface MeritListData {
  studentId?: number;
  studentRollno?: string;
  rollNumber?: string;
  studentName: string;
  fatherName?: string;
  caste?: string;
  gender?: string;
  totalMarks?: number;
  percentage?: number;
  grade?: string;
  className?: string;
  rank?: number;
  promote?: string;
  session?: string;
  [key: string]: any; // Allow additional properties
}

interface MeritListTemplateProps {
  meritListData: MeritListData[];
  schoolName: string;
  district: string;
  block: string;
  diseCode: string;
  mpbseCode: string;
  className: string;
  examType: string;
  sessionYear: string;
  hidePrintButton?: boolean;
}

const MeritListTemplate: React.FC<MeritListTemplateProps> = ({
  meritListData,
  schoolName,
  district,
  block,
  diseCode,
  mpbseCode,
  className,
  examType,
  sessionYear,
  hidePrintButton = false,
}) => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const withCaptureStyles = async <T,>(fn: () => Promise<T>) => {
    const el = templateRef.current;
    if (!el) return fn();

    const prevElStyle = {
      width: el.style.width,
      maxWidth: el.style.maxWidth,
      overflow: el.style.overflow,
    };

    const tableContainers = Array.from(
      el.querySelectorAll<HTMLElement>(".MuiTableContainer-root")
    );
    const prevTableContainerStyles = tableContainers.map((tc) => ({
      el: tc,
      overflowX: tc.style.overflowX,
      overflow: tc.style.overflow,
    }));

    try {
      // Force A4-width layout + avoid horizontal scrollbars during capture.
      el.style.width = "210mm";
      el.style.maxWidth = "210mm";
      el.style.overflow = "visible";

      prevTableContainerStyles.forEach(({ el: tc }) => {
        tc.style.overflowX = "hidden";
        tc.style.overflow = "visible";
      });

      await new Promise((r) => setTimeout(r, 60));
      return await fn();
    } finally {
      el.style.width = prevElStyle.width;
      el.style.maxWidth = prevElStyle.maxWidth;
      el.style.overflow = prevElStyle.overflow;

      prevTableContainerStyles.forEach(({ el: tc, overflowX, overflow }) => {
        tc.style.overflowX = overflowX;
        tc.style.overflow = overflow;
      });
    }
  };

  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    try {
      await withCaptureStyles(async () => {
        if (!templateRef.current) return;
        // Wait for content to render
        await new Promise((r) => setTimeout(r, 120));

        const el = templateRef.current;
        const canvas = await html2canvas(el, {
          background: "#ffffff",
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
          width: el.scrollWidth,
          height: el.scrollHeight,
          logging: false,
        } as any);

        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error("Canvas has zero dimensions - content may not be visible");
        }

        const a = document.createElement("a");
        a.download = `merit-list-${className}-${examType}-${new Date().toISOString().split("T")[0]}.png`;
        a.href = canvas.toDataURL("image/png", 1.0);
        a.click();
      });
    } catch (error) {
      alert(`Failed to download image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdf(true);
    try {
      await withCaptureStyles(async () => {
        await new Promise((r) => setTimeout(r, 80));
        if (!templateRef.current) return;

        const el = templateRef.current;
        const opt = {
          margin: [8, 6, 8, 6],
          filename: `merit-list-${className}-${examType}-${new Date().toISOString().split("T")[0]}.pdf`,
          image: { type: "jpeg", quality: 1.0 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            background: "#ffffff",
            scrollX: 0,
            scrollY: 0,
            windowWidth: el.scrollWidth,
            windowHeight: el.scrollHeight,
            width: el.scrollWidth,
            height: el.scrollHeight,
            logging: false,
          } as any,
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            compress: false,
            putOnlyUsedFonts: true,
          },
          pagebreak: { mode: ["css", "avoid-all"] },
        };
        await html2pdf().set(opt).from(el).save();
      });
    } catch (error) {
      alert("Failed to download PDF.");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          p: {p: 0, sm: 2},
          width: "100%",
          // Mobile: block page-level horizontal scroll; merit area scrolls inside wrapper below.
          overflowX: { xs: "hidden", sm: "visible" },
        }}
      >
        <Box
          sx={{
            overflowX: { xs: "auto", sm: "visible" },
            WebkitOverflowScrolling: "touch",
            width: "100%",
            // Desktop: keep sheet centered (same pattern as marksheet templates).
            display: { xs: "block", sm: "flex" },
            justifyContent: { sm: "center" },
          }}
        >
          <Paper
            ref={templateRef}
            sx={{
              p: 2,
              maxWidth: "210mm",
              // Mobile: sheet keeps A4 width so header + title + table scroll together (not squeezed).
              width: { xs: "max-content", sm: "100%" },
              margin: "0 auto",
              backgroundColor: "#ffffff",
              boxShadow: "none",
              overflow: "visible",
            }}
          >
          <Box
            sx={{
              minWidth: { xs: "210mm", sm: 0 },
              width: { xs: "210mm", sm: "100%" },
            }}
          >
          {/* School Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 2,
              backgroundColor: "#e8f5e8",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: "20px",
                mb: 1.5,
                color: "#2e7d32",
                textTransform: "uppercase",
              }}
            >
              {schoolName}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "start" }}>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  DISTRICT- {district.toUpperCase()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  BLOCK- {block.toUpperCase()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  DISE CODE- {diseCode.toUpperCase()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", fontWeight: 700 }}
                >
                  MPBSE CODE- {mpbseCode.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Notice Board List Title */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 1.5,
              backgroundColor: "#fff3e0",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#e65100",
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "center",
                gap: 3,
              }}
            >
              Merit List <Typography sx={{ fontSize: "17px", color: "#525454" ,fontWeight: "bold",}}>Class:- {className}</Typography>
            </Typography>
          </Box>

          {/* Merit List Table — horizontal scroll is on outer sheet wrapper (mobile); avoid nested scroll */}
          <TableContainer
            sx={{
              overflowX: { xs: "visible", sm: "hidden" },
              width: "100%",
            }}
          >
            <Table
              sx={{
                border: "1px solid #000",
                width: "100%",
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  border: "1px solid #000",
                  padding: "6px",
                  fontSize: "11px",
                  textAlign: "center",
                  fontWeight: "500",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#d0eaf7" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    S.N.
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    ROLL NUMBER
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    STUDENT NAME
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    FATHER NAME
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    CATEGORY
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    GENDER
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    GRAND TOTAL
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    PERCENTAGE
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    GRADE
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    CLASS
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    RANK
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {meritListData.length > 0 ? (
                  meritListData.map((student, index) => (
                    <TableRow
                      key={student.studentId || student.studentRollno || index}
                      sx={{
                        backgroundColor: "#fffef7",
                      }}
                    >
                      <TableCell sx={{ fontWeight: "600" }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.studentRollno || student.rollNumber || ""}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600" }}>
                        {student.studentName}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.fatherName || ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.caste || ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.gender?.toUpperCase() || ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.totalMarks || ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.percentage ? `${student.percentage}%` : ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.grade || ""}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>{student.className || ""}</TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>{student.rank || ""}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show sample data when no real data is available
                  Array.from({ length: 8 }, (_, index) => (
                    <TableRow
                      key={`sample-${index}`}
                      sx={{
                        backgroundColor: "#fffef7",
                      }}
                    >
                      <TableCell sx={{ fontWeight: "600" }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        10A-B{1001 + index}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600" }}>
                        Sample Student {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {480 - index * 5}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {(80 - index * 0.5).toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        A
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        FIRST
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {className}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer with exam type and session */}
          <Box
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: "11px",
              color: "#666",
            }}
          >
            <Typography variant="body2">
              Exam Type: {examType} | Session: {sessionYear}
            </Typography>
          </Box>
          </Box>
        </Paper>
        </Box>
        {!hidePrintButton && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              width: "100%",
              overflowX: { xs: "hidden", sm: "visible" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={downloadAsImage}
              disabled={isDownloadingImage}
              disableRipple
              sx={{ 
                bgcolor: "#1976d2",
                minWidth: { xs: "100%", sm: "64px" },
                maxWidth: "100%",
                "&:hover": {
                  bgcolor: "#1976d2"
                }
              }}
            >
              {isDownloadingImage ? "Downloading..." : "Download as Image"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={downloadAsPdfFromImage}
              disabled={isDownloadingPdf}
              disableRipple
              sx={{ 
                bgcolor: "#d32f2f",
                minWidth: { xs: "100%", sm: "64px" },
                maxWidth: "100%",
                "&:hover": {
                  bgcolor: "#d32f2f"
                }
              }}
            >
              {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MeritListTemplate;
